import React, { useState } from "react";
import {
  Typography,
  FormControl,
  TextField,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from "@material-ui/core";
import ActionButton from "../../components/ActionButton";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import PropertyNames from "../../PropertyNames";
import { useSelector, useDispatch } from "react-redux";
import { setApplicationProperties } from "../../redux/actions/universalActions.js";
import Alert from "@material-ui/lab/Alert";
import axios from "../../AxiosClient";
import Select from "@material-ui/core/Select";

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: 15,
    display: "flex",
  },
  container: {
    height: "100%",
    width: "100%",
  },
  flexContainer: {
    display: "flex",
  },
}));
function ContentSettings() {
  const [saving, setSaving] = useState(false);
  const classes = useStyles();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [updatedProperties, setUpdatedProperties] = useState([]);
  const dispatch = useDispatch();
  const properties = useSelector((state) => ({
    ...state.setting.applicationProperties,
  }));

  const handleCheckboxChange = (propertyKey) => (e) => {
    updateProperties(propertyKey, e.target.checked ? "true" : "false");
  };

  const handleTextFieldChange = (propertyKey) => (e) => {
    updateProperties(propertyKey, e.target.value);
  };

  const updateProperties = (propertyKey, propertyValue) => {
    if (updatedProperties.indexOf(propertyKey) === -1) {
      setUpdatedProperties([...updatedProperties, propertyKey]);
    }
    dispatch(
      setApplicationProperties({
        ...properties,
        [propertyKey]: propertyValue,
      })
    );
  };

  const save = () => {
    setSaving(true);
    let updated = {};
    // so we only patch the changed props and not everything...
    for (const key of updatedProperties) {
      updated[key] = properties[key];
    }
    axios
      .patch("/application-property/", {
        ...updated,
      })
      .then(() => {
        enqueueSnackbar(t("properties_updated_snackbar"), {
          variant: "success",
        });
        setSaving(false);
      })
      .catch((err) => {
        enqueueSnackbar(t("properties_updated_error_snackbar"), {
          variant: "error",
        });
        setSaving(false);
      });
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography variant="h5" style={{ marginRight: 10 }}>
          {t("content_settings_header")}
        </Typography>
        <ActionButton
          variant="contained"
          color="primary"
          onClick={save}
          loading={saving}
        >
          {t("global_save")}
        </ActionButton>
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="h6" gutterBottom>
          {t("content_workflow_settings")}
        </Typography>
        <Typography variant="body2" gutterBottom>
          {t("content_workflow_settings_subheader")}
        </Typography>
        <Alert
          severity="warning"
          style={{ width: "fit-content", marginBottom: 15 }}
        >
          {t("warning_content_workflow_existing_ks")}
        </Alert>
        <FormControl style={{ width: "15%" }}>
          <TextField
            label={t("content_workflow_num_reviewers")}
            inputProps={{ min: "0" }}
            value={
              properties[PropertyNames.REVIEWERS_REQUIRED]
                ? properties[PropertyNames.REVIEWERS_REQUIRED]
                : "1"
            }
            onChange={handleTextFieldChange(PropertyNames.REVIEWERS_REQUIRED)}
            type="number"
          />
        </FormControl>
        <FormControl style={{ width: "15%" }}>
          <TextField
            label={t("content_workflow_num_approvers")}
            inputProps={{ min: "0" }}
            value={
              properties[PropertyNames.APPROVERS_REQUIRED]
                ? properties[PropertyNames.APPROVERS_REQUIRED]
                : "1"
            }
            onChange={handleTextFieldChange(PropertyNames.APPROVERS_REQUIRED)}
            type="number"
          />
        </FormControl>

        <FormControl>
          <FormControlLabel
            label={t("content_workflow_review_own_knowledge")}
            control={
              <Checkbox
                value=""
                checked={
                  properties[PropertyNames.REVIEW_OWN_KNOWLEDGE] === "true"
                }
                onChange={handleCheckboxChange(
                  PropertyNames.REVIEW_OWN_KNOWLEDGE
                )}
                color="primary"
              />
            }
          />
        </FormControl>
        <FormControl>
          <FormControlLabel
            label={t("content_workflow_approve_own_knowledge")}
            control={
              <Checkbox
                value=""
                checked={
                  properties[PropertyNames.APPROVE_OWN_KNOWLEDGE] === "true"
                }
                onChange={handleCheckboxChange(
                  PropertyNames.APPROVE_OWN_KNOWLEDGE
                )}
                color="primary"
              />
            }
          />
        </FormControl>
        <FormControl>
          <FormControlLabel
            label={t("content_workflow_auto_publish")}
            control={
              <Checkbox
                value=""
                checked={properties[PropertyNames.AUTOMATIC_PUBLISH] === "true"}
                onChange={handleCheckboxChange(PropertyNames.AUTOMATIC_PUBLISH)}
                color="primary"
              />
            }
          />
          <FormHelperText>
            {t("content_workflow_auto_publish_helper_text")}
          </FormHelperText>
        </FormControl>
        <FormControl>
          <FormControlLabel
            label={t("content_workflow_autostart_approval")}
            control={
              <Checkbox
                value=""
                checked={
                  properties[PropertyNames.AUTOSTART_APPROVAL] === "true"
                }
                onChange={handleCheckboxChange(
                  PropertyNames.AUTOSTART_APPROVAL
                )}
                color="primary"
              />
            }
          />
          <FormHelperText>
            {t("content_workflow_autostart_approval_helper_text")}
          </FormHelperText>
        </FormControl>

        <Typography variant="h6" gutterBottom>
          {t("viewing_content_settings")}
        </Typography>
        <Typography variant="body2" gutterBottom>
          {t("viewing_content_settings_subheader")}
        </Typography>
        <Alert
          severity="warning"
          style={{ width: "fit-content", marginBottom: 15 }}
        >
          {t("auto_download_warning")}
        </Alert>
        <FormControl>
          <FormControlLabel
            label={t("auto_download_excel_files")}
            control={
              <Checkbox
                value=""
                checked={
                  properties[PropertyNames.AUTO_DOWNLOAD_EXCEL] === "true"
                }
                onChange={handleCheckboxChange(
                  PropertyNames.AUTO_DOWNLOAD_EXCEL
                )}
                color="primary"
              />
            }
          />
          <FormHelperText>
            {t("auto_download_excel_files_helper_text")}
          </FormHelperText>
        </FormControl>
        <FormControl>
          <FormControlLabel
            label={t("auto_download_word_files")}
            control={
              <Checkbox
                value=""
                checked={
                  properties[PropertyNames.AUTO_DOWNLOAD_WORD] === "true"
                }
                onChange={handleCheckboxChange(
                  PropertyNames.AUTO_DOWNLOAD_WORD
                )}
                color="primary"
              />
            }
          />
          <FormHelperText>
            {t("auto_download_word_files_helper_text")}
          </FormHelperText>
        </FormControl>
        <FormControl>
          <FormControlLabel
            label={t("auto_download_ppt_files")}
            control={
              <Checkbox
                value=""
                checked={properties[PropertyNames.AUTO_DOWNLOAD_PPT] === "true"}
                onChange={handleCheckboxChange(PropertyNames.AUTO_DOWNLOAD_PPT)}
                color="primary"
              />
            }
          />
          <FormHelperText>
            {t("auto_download_ppt_files_helper_text")}
          </FormHelperText>
        </FormControl>
        {/* <FormControl>
          <FormControlLabel
            label={t("global_attachment_clean-up")}
            control={
              <Checkbox
                value=""
                checked={
                  properties[PropertyNames.attachments.cleanup] === "true"
                }
                onChange={handleCheckboxChange(
                  PropertyNames.attachments.cleanup
                )}
                color="primary"
              />
            }
          />
          <FormHelperText>
            {t("global_attachment_clean-up_helper_text")}
          </FormHelperText>
        </FormControl> */}

        {/* <FormControl>
          <FormControlLabel
            label={t("global_attachment_interval")}
            control={
              <Select
                value=""
                checked={properties[PropertyNames.attachments.interval]}
                onChange={(evt) =>
                  updateProperties(
                    PropertyNames.attachments.interval,
                    evt.target.value
                  )
                }
                color="primary"
              />
            }
          />
          <FormHelperText>{t("global_attachment_interval")}</FormHelperText>
        </FormControl> */}
      </div>
    </div>
  );
}

export default ContentSettings;
